import { defineStore } from 'pinia'
import { useUserStore } from './user'
import type { OrganizationWorkspaceSummary } from '~/types/Organization'
import { useWorkspaceStore } from '~/stores/workspace'

export const useUserWorkspacesStore = defineStore('userWorkspaces', () => {
  // OTHER STORES ========================================================================
  const userStore = useUserStore()
  const workspaceStore = useWorkspaceStore()

  // STATE ========================================================================
  const recentWorkspaceIds = useLocalStorage<string[]>('recentWorkspaceIds', [])

  // Per-tab recency for back-navigation (useSettingsBackDestination): unlike the
  // localStorage list above (all-time, synced across tabs — kept for the Home
  // picker ordering), this only reflects workspaces visited in this tab since
  // last seeing the workspace chooser (the chooser page clears it on mount), so
  // a fresh tab, a direct entry, or coming from the chooser all fall back to
  // the chooser.
  const sessionRecentWorkspaceIds = useSessionStorage<string[]>(
    'sessionRecentWorkspaceIds',
    [],
  )

  watch(
    () => workspaceStore.currentWorkspaceId,
    (newId) => {
      if (!newId) return

      if (typeof newId !== 'string') return

      unshiftRecentWorkspaceId(newId)
    },
  )

  watch(
    () => userStore.workspaces,
    (newWorkspaces) => {
      if (!newWorkspaces || newWorkspaces.length === 0) return

      recentWorkspaceIds.value = recentWorkspaceIds.value.filter((wsId) =>
        newWorkspaces.some((ws) => ws.id === wsId),
      )
    },
    {
      deep: true,
    },
  )

  const userWorkspaces = computed(() => {
    if (!userStore.user) return []

    const output: OrganizationWorkspaceSummary[] = []

    recentWorkspaceIds.value.forEach((id) => {
      const workspace = userStore.workspaces.find(
        (ws: OrganizationWorkspaceSummary) => ws.id === id,
      )
      if (workspace) output.push(workspace)
    })

    output.push(
      ...userStore.workspaces
        .filter(
          (ws: OrganizationWorkspaceSummary) =>
            !recentWorkspaceIds.value.includes(ws.id),
        )
        .sort(
          (a: OrganizationWorkspaceSummary, b: OrganizationWorkspaceSummary) =>
            a.title.localeCompare(b.title),
        ),
    )

    return output
  })

  function unshiftRecentWorkspaceId(workspaceId: string) {
    recentWorkspaceIds.value = recentWorkspaceIds.value.filter(
      (id) => id !== workspaceId,
    )
    recentWorkspaceIds.value.unshift(workspaceId)

    sessionRecentWorkspaceIds.value = [
      workspaceId,
      ...sessionRecentWorkspaceIds.value.filter((id) => id !== workspaceId),
    ]
  }

  function clearSessionRecentWorkspaceIds() {
    sessionRecentWorkspaceIds.value = []
  }

  return {
    userWorkspaces,
    recentWorkspaceIds,
    sessionRecentWorkspaceIds,
    clearSessionRecentWorkspaceIds,
  }
})
